import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AdminFeaturedLinks — gestione card "Il mondo di OP" (link in home).
 * Incolla un URL → carica l'anteprima del sito (og:image/titolo/descrizione),
 * poi personalizzi titolo, descrizione, testo bottone e (se serve) immagine manuale.
 */
const EMPTY = { url: "", title: "", description: "", button_text: "Vai al sito", manual_image: "", enabled: true };

export default function AdminFeaturedLinks() {
    const [links, setLinks] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [editId, setEditId] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/featured-links/all");
            setLinks(data || []);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        }
    };
    useEffect(() => { load(); }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const reset = () => { setForm(EMPTY); setEditId(null); setPreview(null); setErr(""); };

    const loadPreview = async () => {
        if (!form.url.trim()) return;
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/featured-links/preview", { url: form.url.trim() });
            setPreview(data);
            setForm((f) => ({
                ...f,
                title: f.title || data.title || "",
                description: f.description || data.description || "",
            }));
        } catch (e) {
            setErr(e.response?.data?.detail || "Impossibile caricare l'anteprima");
        } finally {
            setBusy(false);
        }
    };

    const save = async () => {
        if (!form.url.trim() || !form.title.trim()) { setErr("URL e titolo sono obbligatori"); return; }
        setBusy(true); setErr("");
        try {
            if (editId) await api.put(`/featured-links/${editId}`, form);
            else await api.post("/featured-links", form);
            reset();
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const edit = (l) => {
        setEditId(l.id);
        setForm({ url: l.url, title: l.title, description: l.description, button_text: l.button_text, manual_image: l.manual_image, enabled: l.enabled });
        setPreview({ image: l.og_image });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare questa card?")) return;
        await api.delete(`/featured-links/${id}`);
        await load();
    };

    const toggle = async (l) => {
        await api.put(`/featured-links/${l.id}`, { ...l, enabled: !l.enabled });
        await load();
    };

    const effImage = form.manual_image || preview?.image || "";

    return (
        <div data-testid="admin-featured-links">
            <div className="op-label text-t2bb6ff mb-1">// IL MONDO DI OP</div>
            <h1 className="op-heading text-3xl text-tffffff mb-2">Card & link in home</h1>
            <p className="text-t8f95a3 text-sm max-w-3xl mb-6 leading-relaxed">
                Incolla il link di un sito (altro torneo, canale Telegram, pagina Facebook…), carica l'anteprima
                per prendere automaticamente l'immagine del sito, poi scrivi titolo, descrizione e il testo del bottone.
            </p>

            {err && <div className="op-card p-3 border-tff3b30 text-tff3b30 text-sm mb-4" data-testid="fl-error">{err}</div>}

            {/* FORM */}
            <div className="op-card p-4 mb-8" data-testid="fl-form">
                <div className="op-label text-te8ff00 mb-3">{editId ? "// MODIFICA CARD" : "// NUOVA CARD"}</div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                        <div>
                            <label className="op-label text-[10px]">Link (URL)</label>
                            <div className="flex gap-2 mt-1">
                                <input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" className="op-input w-full text-sm" data-testid="fl-url" />
                                <button onClick={loadPreview} disabled={busy} className="op-btn op-btn-ghost text-xs whitespace-nowrap" data-testid="fl-preview-btn">Carica anteprima</button>
                            </div>
                        </div>
                        <div>
                            <label className="op-label text-[10px]">Titolo</label>
                            <input value={form.title} onChange={(e) => set("title", e.target.value)} className="op-input w-full text-sm mt-1" data-testid="fl-title" />
                        </div>
                        <div>
                            <label className="op-label text-[10px]">Descrizione</label>
                            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="op-input w-full text-sm mt-1" data-testid="fl-description" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="op-label text-[10px]">Testo bottone</label>
                                <input value={form.button_text} onChange={(e) => set("button_text", e.target.value)} placeholder="Vai al sito" className="op-input w-full text-sm mt-1" data-testid="fl-button-text" />
                            </div>
                            <label className="flex items-end gap-2 pb-2 cursor-pointer">
                                <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="fl-enabled" />
                                <span className="op-mono text-[12px] text-tc9ccd3">Visibile in home</span>
                            </label>
                        </div>
                        <div>
                            <label className="op-label text-[10px]">Immagine manuale (ripiego, opzionale)</label>
                            <input value={form.manual_image} onChange={(e) => set("manual_image", e.target.value)} placeholder="URL immagine se il sito non ne espone una" className="op-input w-full text-sm mt-1" data-testid="fl-manual-image" />
                        </div>
                    </div>
                    {/* Anteprima card */}
                    <div>
                        <label className="op-label text-[10px] mb-1 block">Anteprima</label>
                        <div className="op-card overflow-hidden">
                            <div className="h-36 bg-t0a0b0d flex items-center justify-center overflow-hidden">
                                {effImage ? (
                                    <img src={effImage} alt="" className="w-full h-full object-cover" data-testid="fl-preview-img" />
                                ) : (
                                    <span className="op-heading text-2xl text-t22252a">OP</span>
                                )}
                            </div>
                            <div className="p-3">
                                <div className="op-heading text-tffffff text-sm">{form.title || "Titolo della card"}</div>
                                {form.description && <p className="text-t8f95a3 text-xs mt-1 line-clamp-3">{form.description}</p>}
                                <span className="mt-3 inline-block op-btn op-btn-primary text-[11px]">{form.button_text || "Vai al sito"}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex gap-3 mt-4">
                    <button onClick={save} disabled={busy} className="op-btn op-btn-primary text-sm" data-testid="fl-save-btn">
                        {busy ? "…" : editId ? "Salva modifiche" : "Aggiungi card"}
                    </button>
                    {editId && <button onClick={reset} className="op-btn op-btn-ghost text-sm" data-testid="fl-cancel-btn">Annulla</button>}
                </div>
            </div>

            {/* LISTA */}
            <div className="op-label text-t8f95a3 mb-3">// CARD ESISTENTI ({links.length})</div>
            <div className="space-y-3">
                {links.map((l) => (
                    <div key={l.id} className="op-card p-3 flex items-center gap-3" data-testid={`fl-row-${l.id}`}>
                        <div className="w-16 h-12 bg-t0a0b0d overflow-hidden shrink-0 flex items-center justify-center">
                            {l.image_url ? <img src={l.image_url} alt="" className="w-full h-full object-cover" /> : <span className="op-mono text-[10px] text-t5c6370">no img</span>}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-tffffff text-sm truncate">{l.title} {!l.enabled && <span className="op-mono text-[9px] text-tff7a1a">(nascosta)</span>}</div>
                            <div className="op-mono text-[10px] text-t5c6370 truncate">{l.url}</div>
                        </div>
                        <button onClick={() => toggle(l)} className="op-btn op-btn-ghost text-[11px]" data-testid={`fl-toggle-${l.id}`}>{l.enabled ? "Nascondi" : "Mostra"}</button>
                        <button onClick={() => edit(l)} className="op-btn op-btn-ghost text-[11px]" data-testid={`fl-edit-${l.id}`}>Modifica</button>
                        <button onClick={() => remove(l.id)} className="op-btn text-[11px] border-tff4d4d text-tff4d4d hover:bg-tff4d4d/10" data-testid={`fl-delete-${l.id}`}>Elimina</button>
                    </div>
                ))}
                {links.length === 0 && <div className="op-card p-5 text-t8f95a3 text-sm">Nessuna card. Aggiungi la prima qui sopra.</div>}
            </div>
        </div>
    );
}
